import React, { forwardRef } from 'react';
import { ITextField } from './interfaces';
import { FormField } from '../FormField';
import { TextField as TextFieldMui, Typography } from '@mui/material';

export const TextField = forwardRef<HTMLInputElement, ITextField>(
  (
    {
      label,
      helperText,
      placeholder,
      required,
      multiline,
      error,
      value,
      type,
      maxChars,
      minChars,
      disabled,
      children,
      endAdornment,
      flex,
      onChange,
    },
    ref
  ): JSX.Element => {
    const normalizedValue =
      typeof value === 'string' ? value : value == null ? '' : String(value);
    const length = normalizedValue.length;
    const trimmedLength = normalizedValue.trim().length;
    const hasMaxError = !!maxChars && length > maxChars;
    const hasMinError = !!minChars && trimmedLength < minChars;
    const showCharCounter = !!minChars || !!maxChars;

    const charCounterLabel = ((): string => {
      if (minChars && maxChars) {
        return `${trimmedLength} / ${minChars}–${maxChars}`;
      }

      if (minChars) {
        return `${trimmedLength} / ${minChars}`;
      }

      return `${length} / ${maxChars}`;
    })();

    return (
      <FormField
        label={label}
        helperText={helperText}
        required={required}
        error={error}
        flex={flex}
      >
        <TextFieldMui
          inputRef={ref}
          size="small"
          type={type}
          placeholder={placeholder}
          error={!!error || hasMaxError || hasMinError}
          value={normalizedValue}
          multiline={multiline}
          disabled={disabled}
          onChange={(event) => {
            if (onChange) {
              onChange(event.target.value);
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#fff',
            },
          }}
          slotProps={{
            input: {
              endAdornment: endAdornment,
            },
          }}
        />
        {showCharCounter && (
          <Typography
            variant="body2"
            color={
              hasMaxError || hasMinError ? 'error' : 'text.secondary'
            }
            sx={{
              position: 'absolute',
              top: '-23px',
              right: 0,
            }}
          >
            {charCounterLabel}
          </Typography>
        )}
        {children}
      </FormField>
    );
  }
);
